import React, { useState, useEffect } from "react";

const AIVYBenchmarkChart = () => {
  const [animationProgress, setAnimationProgress] = useState(0);
  const [hoveredModel, setHoveredModel] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationProgress(1);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const benchmarks = [
    { name: "HumanEval", aivy001: 85.4, aivy002: 87.8, aivyPro: 89.5 },
    { name: "GSM8K", aivy001: 94.4, aivy002: 95.9, aivyPro: 97.0 },
    { name: "MMLU (Pro)", aivy001: 60.6, aivy002: 67.5, aivyPro: 70.0 },
    { name: "IFEval", aivy001: 88.9, aivy002: 90.4, aivyPro: 92.0 },
    { name: "FACTS", aivy001: 75.8, aivy002: 74.9, aivyPro: 78.5 },
    { name: "BIG-Bench", aivy001: 85.7, aivy002: 87.6, aivyPro: 89.0 },
  ];

  const models = [
    { key: "aivy001", name: "aivy-001", color: "#1e3a8a" },
    { key: "aivy002", name: "aivy-002", color: "#3b82f6" },
    { key: "aivyPro", name: "aivy-pro", color: "#60a5fa" },
  ];

  return (
    <div className="chart-container">
      <style>{`
        .chart-container {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          padding: 30px;
          border-radius: 20px;
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.5);
          width: 100%;
          max-width: 900px;
          margin: 0 auto;
        }
        
        .chart-header {
          text-align: center;
          margin-bottom: 30px;
        }
        
        .chart-title {
          font-size: 24px;
          font-weight: 700;
          color: #f1f5f9;
          margin-bottom: 5px;
          background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .chart-subtitle {
          font-size: 13px;
          color: #64748b;
        }
        
        .chart-grid {
          display: grid;
          gap: 12px;
        }
        
        .benchmark-row {
          display: grid;
          grid-template-columns: 100px 1fr;
          gap: 15px;
          align-items: center;
        }
        
        .benchmark-name {
          font-size: 12px;
          font-weight: 600;
          color: #cbd5e1;
          text-align: right;
        }
        
        .bars-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
          position: relative;
          background: rgba(30, 41, 59, 0.3);
          padding: 8px;
          border-radius: 8px;
          backdrop-filter: blur(10px);
        }
        
        .bar-wrapper {
          position: relative;
          height: 16px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          overflow: hidden;
        }
        
        .bar {
          height: 100%;
          border-radius: 8px;
          position: relative;
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 2px rgba(0, 0, 0, 0.2);
        }
        
        .bar::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transform: translateX(-100%);
          animation: shimmer 2s infinite;
        }
        
        @keyframes shimmer {
          100% {
            transform: translateX(100%);
          }
        }
        
        .bar-label {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 10px;
          font-weight: 600;
          color: white;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
        }
        
        .legend {
          display: flex;
          justify-content: center;
          gap: 20px;
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .legend-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #94a3b8;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .legend-item:hover {
          color: #cbd5e1;
        }
        
        .legend-item.active {
          color: #f1f5f9;
        }
        
        .legend-color {
          width: 12px;
          height: 12px;
          border-radius: 3px;
          transition: all 0.2s ease;
        }
        
        .legend-item:hover .legend-color,
        .legend-item.active .legend-color {
          transform: scale(1.2);
          box-shadow: 0 0 10px currentColor;
        }
        
        .grid-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          pointer-events: none;
        }
        
        .grid-line {
          flex: 1;
          border-right: 1px solid rgba(255, 255, 255, 0.03);
        }
        
        .grid-line:last-child {
          border-right: none;
        }
      `}</style>

      <div className="chart-header">
        <h1 className="chart-title">AIVY Model Benchmark Comparison</h1>
        <p className="chart-subtitle">
          Performance across evaluation benchmarks
        </p>
      </div>

      <div className="chart-grid">
        {benchmarks.map((benchmark, index) => (
          <div key={benchmark.name} className="benchmark-row">
            <div className="benchmark-name">{benchmark.name}</div>
            <div className="bars-group">
              <div className="grid-overlay">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="grid-line" />
                ))}
              </div>
              {models.map((model, modelIndex) => (
                <div key={model.key} className="bar-wrapper">
                  <div
                    className="bar"
                    style={{
                      width: `${benchmark[model.key] * animationProgress}%`,
                      background: `linear-gradient(90deg, ${model.color} 0%, ${model.color}dd 100%)`,
                      transitionDelay: `${index * 50 + modelIndex * 100}ms`,
                      opacity:
                        hoveredModel && hoveredModel !== model.key ? 0.5 : 1,
                    }}
                    onMouseEnter={() => setHoveredModel(model.key)}
                    onMouseLeave={() => setHoveredModel(null)}
                  >
                    <span className="bar-label">{benchmark[model.key]}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="legend">
        {models.map((model) => (
          <div
            key={model.key}
            className={`legend-item ${
              hoveredModel === model.key ? "active" : ""
            }`}
            onMouseEnter={() => setHoveredModel(model.key)}
            onMouseLeave={() => setHoveredModel(null)}
          >
            <div
              className="legend-color"
              style={{ backgroundColor: model.color }}
            />
            <span>{model.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AIVYBenchmarkChart;
